import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { X } from 'lucide-react'
import { DialogContent, DialogClose } from '@/components/ui/dialog'
import { cn } from '@/lib/utils'

/** Shared close button used in Board Info, Ticket Detail, Attachment preview, and App Info dialogs. */
export function DialogCloseButton() {
  const { t } = useTranslation()
  return (
    <div className="absolute top-4 right-4 z-50 p-4 bg-background/90 rounded-md">
      <DialogClose className="rounded-md opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring/40 focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground border border-border bg-background p-1.5">
        <X className="size-5" />
        <span className="sr-only">{t('common.close')}</span>
      </DialogClose>
    </div>
  )
}

const SIZE_CLASSES = {
  narrow:
    'sm:max-w-2xl w-[calc(100%-1.5rem)] max-h-[96dvh] flex flex-col gap-0 overflow-hidden p-0 sm:w-[calc(100%-2rem)] max-md:top-[max(1rem,2dvh)] max-md:translate-y-0 max-md:pt-0!',
  wide:
    'max-w-6xl w-[calc(100%-1.5rem)] max-h-[96dvh] flex flex-col gap-0 overflow-hidden p-0 sm:w-[calc(100%-2rem)] max-md:top-[max(1rem,2dvh)] max-md:translate-y-0 max-md:pt-0!'
} as const

interface ResponsiveDialogContentProps
  extends Omit<React.ComponentProps<typeof DialogContent>, 'hideClose'> {
  size?: 'narrow' | 'wide'
}

const ResponsiveDialogContent = React.forwardRef<
  React.ElementRef<typeof DialogContent>,
  ResponsiveDialogContentProps
>(({ className, children, size = 'wide', ...props }, ref) => {
  return (
    <DialogContent
      ref={ref}
      hideClose
      className={cn(SIZE_CLASSES[size], className)}
      {...props}
    >
      <DialogCloseButton />
      {/* Scrollable content: min-h-0 allows flex child to shrink; min-w-0 + overflow-x-hidden prevent horizontal shift */}
      <div className="flex-1 min-h-0 min-w-0 overflow-y-auto overflow-x-hidden p-6 [scrollbar-gutter:stable]">
        {children}
      </div>
    </DialogContent>
  )
})
ResponsiveDialogContent.displayName = 'ResponsiveDialogContent'

export { ResponsiveDialogContent }
